import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Gavel, ArrowRight, PencilSimple, Rocket, Trash, Play, Stop,
  ClockCountdown, CheckCircle, XCircle, GitFork, Eye, Sparkle, Star, Copy,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";

function StatusChip({ status }) {
  const map = {
    draft: "chip-neutral",
    published: "chip-low",
    archived: "chip-neutral",
    running: "chip-medium",
    completed: "chip-low",
    rejected: "chip-high",
    cancelled: "chip-neutral",
  };
  return <span className={`chip ${map[status] || "chip-neutral"}`}>{status}</span>;
}

function WorkflowCard({ wf, nav, onPublish, onDelete, onStart, onNewVersion }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <GlassCard hover className="p-6 h-full flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <Gavel size={22} weight="duotone" className="text-[var(--accent)]" />
          <div className="flex items-center gap-1">
            <span className="chip chip-neutral font-mono">v{wf.version}</span>
            <StatusChip status={wf.status} />
          </div>
        </div>
        <h3 className="text-lg font-medium leading-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{wf.name}</h3>
        <div className="text-xs text-[var(--text-3)] mb-3">{wf.description || "No description"}</div>

        <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
          <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
            <div className="text-[9px] uppercase tracking-widest text-[var(--text-3)]">Nodes</div>
            <div className="font-mono text-base mt-0.5">{wf.nodes?.length || 0}</div>
          </div>
          <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
            <div className="text-[9px] uppercase tracking-widest text-[var(--text-3)]">Edges</div>
            <div className="font-mono text-base mt-0.5">{wf.edges?.length || 0}</div>
          </div>
        </div>

        <div className="mt-auto flex flex-wrap gap-2">
          {wf.status === "draft" && (
            <>
              <Button size="sm" variant="outline" onClick={() => nav(`/app/workflows/designer/${wf.id}`)} data-testid={`wf-edit-${wf.id}`} className="gap-1 glass-subtle border-[var(--border-strong)]">
                <PencilSimple size={12} /> Edit
              </Button>
              <Button size="sm" onClick={() => onPublish(wf.id)} data-testid={`wf-publish-${wf.id}`} className="gap-1">
                <Rocket size={12} /> Publish
              </Button>
            </>
          )}
          {wf.status === "published" && (
            <>
              <Button size="sm" variant="outline" onClick={() => nav(`/app/workflows/designer/${wf.id}`)} data-testid={`wf-view-${wf.id}`} className="gap-1 glass-subtle border-[var(--border-strong)]">
                <Eye size={12} /> View
              </Button>
              <Button size="sm" onClick={() => onStart(wf)} data-testid={`wf-start-${wf.id}`} className="gap-1">
                <Play size={12} weight="fill" /> Start instance
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onNewVersion(wf.id)} data-testid={`wf-newver-${wf.id}`} className="gap-1">
                <GitFork size={12} /> New version
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(wf.id)} data-testid={`wf-delete-${wf.id}`} className="text-[var(--danger)] ms-auto">
            <Trash size={12} />
          </Button>
        </div>
      </GlassCard>
    </motion.div>
  );
}

export default function Workflows() {
  const nav = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("published");
  const [openNew, setOpenNew] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startingWf, setStartingWf] = useState(null);
  const [contracts, setContracts] = useState([]);

  const load = async () => {
    const [w, i, c, t] = await Promise.all([
      api.get("/workflows"),
      api.get("/instances"),
      api.get("/contracts"),
      api.get("/workflow-templates"),
    ]);
    setWorkflows(w.data); setInstances(i.data); setContracts(c.data); setTemplates(t.data.templates);
  };
  useEffect(() => { load(); }, []);

  const applyTemplate = async (templateId) => {
    try {
      const { data } = await api.post(`/workflow-templates/${templateId}/clone`);
      toast.success(`Template "${data.name}" cloned as draft`);
      nav(`/app/workflows/designer/${data.id}`);
    } catch (e) { toast.error("Clone failed"); }
  };

  const create = async () => {
    if (!name) return toast.error("Name required");
    const { data } = await api.post("/workflows", { name, description });
    toast.success("Draft created");
    setOpenNew(false); setName(""); setDescription("");
    nav(`/app/workflows/designer/${data.id}`);
  };

  const publish = async (id) => {
    try {
      await api.post(`/workflows/${id}/publish`);
      toast.success("Published");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete / archive this workflow?")) return;
    await api.delete(`/workflows/${id}`);
    toast.success("Done");
    load();
  };

  const newVersion = async (id) => {
    const { data } = await api.post(`/workflows/${id}/new-version`);
    toast.success("New draft ready");
    nav(`/app/workflows/designer/${data.id}`);
  };

  const startInstance = async (contractId) => {
    if (!startingWf) return;
    try {
      const { data } = await api.post(`/workflows/${startingWf.id}/start`, { contract_id: contractId });
      toast.success("Instance started");
      setStartingWf(null);
      nav(`/app/workflows/instances/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = workflows.filter((w) => tab === "all" ? true : w.status === tab);
  const wfSort = useSearchSort(filtered, {
    searchKeys: ["name", "description"],
    sortOptions: [
      { value: "created_at", label: "Newest", defaultDir: "desc" },
      { value: "name", label: "Name A→Z", defaultDir: "asc" },
      { value: "version", label: "Version", defaultDir: "desc" },
    ],
    defaultSort: "created_at",
  });
  const instSort = useSearchSort(instances, {
    searchKeys: ["name", "workflow_name"],
    sortOptions: [
      { value: "started_at", label: "Started", defaultDir: "desc" },
      { value: "name", label: "Name A→Z", defaultDir: "asc" },
      { value: "status", label: "Status", defaultDir: "asc" },
    ],
    defaultSort: "started_at",
  });
  const tplSort = useSearchSort(templates, {
    searchKeys: ["name", "description", "industry", "category"],
    sortOptions: [
      { value: "popularity", label: "Popularity", defaultDir: "desc" },
      { value: "name", label: "Name A→Z", defaultDir: "asc" },
      { value: "industry", label: "Industry", defaultDir: "asc" },
    ],
    defaultSort: "popularity",
  });

  return (
    <PageShell
      title="Workflow Studio"
      subtitle="Design, publish, and monitor approval workflows"
      right={
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-workflow-btn" className="gap-2"><Plus size={14} /> New workflow</Button>
          </DialogTrigger>
          <DialogContent className="glass-strong border-0 max-w-md">
            <DialogHeader><DialogTitle>Create workflow</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <div className="overline mb-1">Name</div>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Procurement approval — contracts > $100k" data-testid="wf-name-input" className="glass-subtle border-[var(--border)]" />
              </div>
              <div>
                <div className="overline mb-1">Description</div>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" data-testid="wf-desc-input" className="glass-subtle border-[var(--border)]" />
              </div>
              <Button onClick={create} data-testid="wf-create-btn" className="w-full gap-2">Open in designer <ArrowRight size={14} /></Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="glass-subtle p-1 flex-wrap h-auto">
          <TabsTrigger value="published" data-testid="tab-published">Published ({workflows.filter((w) => w.status === "published").length})</TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-drafts">Drafts ({workflows.filter((w) => w.status === "draft").length})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">Archived ({workflows.filter((w) => w.status === "archived").length})</TabsTrigger>
          <TabsTrigger value="instances" data-testid="tab-instances">Instances ({instances.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
        </TabsList>

        {["published", "draft", "archived"].map((s) => (
          <TabsContent key={s} value={s}>
            <SearchSortBar controller={wfSort} placeholder="Search workflows by name or description…" testIdPrefix="wf" />
            {wfSort.items.length === 0 ? (
              <GlassCard className="p-12 text-center">
                <Gavel size={32} weight="duotone" className="mx-auto mb-4 text-[var(--accent)]" />
                <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No {s} workflows{wfSort.q ? " matching your search" : ""}</h3>
                <p className="text-sm text-[var(--text-2)]">{s === "draft" ? "Create a new workflow to start designing." : s === "published" ? "Publish a draft to make it available for instances." : "Archived workflows appear here."}</p>
              </GlassCard>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {wfSort.items.map((wf) => (
                  <WorkflowCard key={wf.id} wf={wf} nav={nav} onPublish={publish} onDelete={del} onStart={setStartingWf} onNewVersion={newVersion} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="instances">
          <SearchSortBar controller={instSort} placeholder="Search instances by name or workflow…" testIdPrefix="inst" />
          {instSort.items.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <Play size={32} weight="duotone" className="mx-auto mb-4 text-[var(--accent)]" />
              <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No instances yet</h3>
              <p className="text-sm text-[var(--text-2)]">Publish a workflow and start an instance to track it here.</p>
            </GlassCard>
          ) : (
            <GlassCard className="p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--surface-2)] border-b border-[var(--border)]">
                    <tr>
                      <th className="text-start p-4 overline">Instance</th>
                      <th className="text-start p-4 overline">Workflow</th>
                      <th className="text-start p-4 overline">Status</th>
                      <th className="text-start p-4 overline">Current stage</th>
                      <th className="text-start p-4 overline">Started</th>
                      <th className="text-end p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {instSort.items.map((inst) => {
                      const currentLabel = inst.history?.find((h) => h.node_id === inst.current_node_id)?.node_label || inst.current_node_id;
                      return (
                        <tr key={inst.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)] transition" data-testid={`inst-row-${inst.id}`}>
                          <td className="p-4 font-medium">{inst.name}</td>
                          <td className="p-4 text-[var(--text-2)]">{inst.workflow_name} <span className="font-mono text-xs">v{inst.workflow_version}</span></td>
                          <td className="p-4"><StatusChip status={inst.status} /></td>
                          <td className="p-4 text-[var(--text-2)]">{currentLabel}</td>
                          <td className="p-4 font-mono text-xs text-[var(--text-3)]">{new Date(inst.started_at).toLocaleString()}</td>
                          <td className="p-4 text-end">
                            <Button size="sm" variant="ghost" onClick={() => nav(`/app/workflows/instances/${inst.id}`)} data-testid={`inst-view-${inst.id}`} className="gap-1">
                              View <ArrowRight size={12} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          )}
        </TabsContent>

        <TabsContent value="templates">
          <SearchSortBar controller={tplSort} placeholder="Search templates by name, industry, or category…" testIdPrefix="tpl" />
          {tplSort.items.length === 0 ? (
            <GlassCard className="p-12 text-center"><Sparkle size={32} weight="duotone" className="mx-auto mb-4 text-[var(--accent)]" /><div>{tplSort.q ? "No templates match your search." : "No templates loaded."}</div></GlassCard>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {tplSort.items.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <GlassCard hover className="p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <Sparkle size={22} weight="duotone" className="text-[var(--accent)]" />
                      <div className="flex items-center gap-1">
                        <Star size={12} weight="fill" className="text-amber-400" />
                        <span className="text-xs font-mono text-[var(--text-2)]">{t.popularity}%</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-medium leading-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{t.name}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="chip chip-neutral text-[10px]">{t.industry}</span>
                      <span className="chip chip-neutral text-[10px]">{t.category}</span>
                    </div>
                    <div className="text-xs text-[var(--text-2)] leading-relaxed mb-4 flex-1">{t.description}</div>
                    <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                      <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-center">
                        <div className="font-mono font-semibold">{t.nodes?.length || 0}</div>
                        <div className="text-[9px] uppercase tracking-widest text-[var(--text-3)]">Steps</div>
                      </div>
                      <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-center">
                        <div className="font-mono font-semibold">{t.edges?.length || 0}</div>
                        <div className="text-[9px] uppercase tracking-widest text-[var(--text-3)]">Transitions</div>
                      </div>
                    </div>
                    <Button onClick={() => applyTemplate(t.id)} data-testid={`template-use-${t.id}`} className="gap-2 w-full">
                      <Copy size={12} /> Use this template
                    </Button>
                  </GlassCard>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Start-instance dialog */}
      <Dialog open={!!startingWf} onOpenChange={(o) => !o && setStartingWf(null)}>
        <DialogContent className="glass-strong border-0 max-w-md">
          <DialogHeader><DialogTitle>Start instance of "{startingWf?.name}"</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
            <div className="text-xs text-[var(--text-2)] mb-2">Pick a contract to attach (optional):</div>
            <button onClick={() => startInstance(null)} data-testid="start-instance-no-contract-btn" className="w-full text-left p-3 rounded-md bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--border-strong)] transition text-sm">
              No contract (ad-hoc)
            </button>
            {contracts.map((c) => (
              <button key={c.id} onClick={() => startInstance(c.id)} data-testid={`start-instance-contract-${c.id}`} className="w-full text-left p-3 rounded-md bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--border-strong)] transition text-sm">
                <div className="font-medium truncate">{c.title}</div>
                <div className="text-xs text-[var(--text-3)]">{c.counterparty}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
